import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

import ReportesApi from "api/reportesApi";
import TablaReporte from "./TablaReporte";
import ModalGrafico from "./ModalGrafico";
import { exportarAExcel, prepararDatosParaExportacion } from "../utils/exportarExcel";
import { exportarAPDF, formatters } from "../utils/exportarPDF";

/**
 * Componente de reporte de procesos por sede
 */
function ReportePorSede({ filtros = {} }) {
  const [datos, setDatos] = useState([]);
  const [totales, setTotales] = useState({});
  const [datosGrafico, setDatosGrafico] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalGraficoAbierto, setModalGraficoAbierto] = useState(false);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ReportesApi.obtenerReportePorSede(filtros);
      setDatos(response.data.datos || []);
      setTotales(response.data.totales || {});
      setDatosGrafico(response.data.grafico || {});
    } catch (error) {
      console.error("Error al cargar reporte por sede:", error);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const columnas = [
    {
      header: "Sede",
      field: "sede_nombre",
      align: "left",
      width: "70%",
    },
    {
      header: "Procesos",
      field: "total_procesos",
      align: "center",
      width: "30%",
      format: (valor) => formatters.numero(valor),
    },
  ];

  const handleExportarExcel = () => {
    const datosFormateados = prepararDatosParaExportacion(
      datos.map((item) => ({
        Sede: item.sede_nombre,
        "Total Procesos": item.total_procesos,
      })),
      {
        "Total Procesos": "numero",
      }
    );

    exportarAExcel(datosFormateados, "Reporte_Por_Sede", "Sedes", {
      titulo: "Reporte de Procesos por Sede",
      filtros: filtros,
    });
  };

  const handleExportarPDF = () => {
    exportarAPDF({
      titulo: "Reporte de Procesos por Sede",
      columnas: columnas,
      datos: datos,
      filtros: filtros,
      totales: {
        "Total de Procesos": formatters.numero(totales.total_procesos || 0),
      },
    });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={4}>
        <Card
          sx={{
            background: "linear-gradient(195deg, #49a3f1 0%, #1A73E8 100%)",
          }}
        >
          <SuiBox p={2} textAlign="center">
            <SuiTypography variant="h3" fontWeight="bold" sx={{ color: "white !important" }}>
              {formatters.numero(totales.total_procesos || 0)}
            </SuiTypography>
            <SuiTypography
              variant="button"
              sx={{ color: "white !important", opacity: 0.9 }}
              fontWeight="medium"
            >
              Procesos
            </SuiTypography>
          </SuiBox>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <TablaReporte
          titulo="Reporte por Sede"
          columnas={columnas}
          datos={datos}
          loading={loading}
          onVerGrafico={() => setModalGraficoAbierto(true)}
          onExportarExcel={handleExportarExcel}
          onExportarPDF={handleExportarPDF}
        />
      </Grid>

      <ModalGrafico
        abierto={modalGraficoAbierto}
        onCerrar={() => setModalGraficoAbierto(false)}
        titulo="Gráfico de Procesos por Sede"
        tipoGrafico="pie"
        datosGrafico={datosGrafico}
      />
    </Grid>
  );
}

ReportePorSede.propTypes = {
  filtros: PropTypes.object,
};

export default ReportePorSede;
